// The site-level (non-brand) configuration for a distributor: identity, copy,
// supported locales/currencies and assets. The visual brand lives separately in
// each distributor's `brand.json` (a `@coras-io/brand-tokens` BrandConfig).

/**
 * The locales a microsite ships unless it names its own set. Nearly every
 * distributor shipped the same set, so it is defined once here; a distributor
 * only picks its *default* locale (`SiteConfig.locale`). The strings are a
 * subset of the SDK's `SupportedLocales`; the microsite casts at the `mount()`
 * boundary.
 */
export const DEFAULT_LOCALES = [
  "da-DK",
  "de-DE",
  "en-GB",
  "en-IE",
  "en-US",
  "es-ES",
  "fr-FR",
  "fr-MA",
  "is-IS",
  "it-IT",
  "ms-MY",
  "nb-NO",
  "pl-PL",
  "pt-PT",
  "sv-SE",
  "th-TH",
  "tr-TR",
  "zh-CN",
] as const;

/** The currencies every microsite supports today (see DEFAULT_LOCALES). */
export const DEFAULT_CURRENCIES = [
  "AED",
  "ALL",
  "AUD",
  "CHF",
  "CNY",
  "DKK",
  "EUR",
  "GBP",
  "HUF",
  "IDR",
  "ISK",
  "JPY",
  "MAD",
  "MYR",
  "NOK",
  "PHP",
  "PLN",
  "SEK",
  "SGD",
  "THB",
  "TRY",
  "USD",
  "VND",
] as const;

/**
 * Every locale a site may list: the shared set plus the ones enabled for
 * individual distributors. `ar-SA` is Riyadh Air's alone - the SDK renders
 * left-to-right only, so Arabic goes to the distributor that asked for it
 * rather than to every site.
 */
export const SITE_LOCALES = [...DEFAULT_LOCALES, "ar-SA"] as const;

export type SiteLocale = (typeof SITE_LOCALES)[number];

/**
 * Every currency a site may list: the shared set plus the ones enabled for
 * individual distributors. `SAR` is Riyadh Air's alone, for the same reason
 * `ar-SA` is (see SITE_LOCALES).
 */
export const SITE_CURRENCIES = [...DEFAULT_CURRENCIES, "SAR"] as const;

export type SiteCurrency = (typeof SITE_CURRENCIES)[number];

/** How a loyalty programme reads in one locale. */
export type LoyaltyProgrammeText = {
  name: string;
  unit: { singular: string; plural: string };
  /** Wording for the checkout fields the API lists, keyed by API field name; the label is shown as the field's placeholder. */
  fields?: Record<string, { label: string; helpText?: string }>;
};

/**
 * The distributor's partner loyalty programme as the site presents it. The API
 * decides the amounts and which checkout fields exist; this names the
 * programme code to read them for and supplies the words and artwork. Passed
 * to the embed as `config.loyalty`.
 */
export type LoyaltyProgramme = {
  /** Programme code; the key of `loyalty.earn` values in API responses. */
  code: string;
  /** Artwork paths relative to the brand folder (`assets/images/...`). */
  logo?: string;
  logoDark?: string;
  icon?: string;
  termsUrl?: string;
  /** Copy per locale; the site's default locale is the fallback. */
  text: Partial<Record<SiteLocale, LoyaltyProgrammeText>>;
};

/**
 * The notice an EU airline must show beside bookings made through a link from
 * its own site: Directive (EU) 2015/2302 on package travel and linked travel
 * arrangements. The microsite renders it as a dismissible card at the bottom
 * of every page; the embed carries no such copy. The wording is fixed by the
 * directive, so a site supplies only the names and the link.
 */
export type LinkedTravelNotice = {
  /** The legal entity the notice names, e.g. "Deutsche Lufthansa AG". */
  company: string;
  /**
   * The possessive the English text uses in "because of X insolvency", e.g.
   * "Deutsche Lufthansa's" or "Austrian Airlines'". German declines the
   * company name itself and does not use this.
   */
  companyPossessive: string;
  /** The airline's insolvency protection page per locale; `default` is the fallback. */
  insolvencyProtectionUrl: { default: string } & Partial<
    Record<SiteLocale, string>
  >;
};

export type SiteConfig = {
  /**
   * The Coras API distributor id (a UUID). Also the `clientId` the emails
   * service keys brands by, so it is the CDN filename a brand publishes to.
   */
  distributorId: string;
  /** Browser/tab title and og:site_name. */
  title: string;
  description: string;
  /** Default locale/currency when detection is off or finds no match. */
  locale: SiteLocale;
  currency: SiteCurrency;
  locales: readonly SiteLocale[];
  currencies: readonly SiteCurrency[];
  /** Favicon filenames under `/assets/favicons` (copied from the brand assets). */
  favicons: string[];
  /** Same-origin logo path the microsite renders into the navbar brand slot. */
  logo: string;
  /** Production hostname; also the assets origin (`https://<domain>/assets`). */
  domain: string;
  /**
   * The distributor's main website, for a booking site that sits beside one
   * (`experiences.riyadhair.com` beside `riyadhair.com`). Set it and the navbar
   * logo links there, the way it does on the main site. Unset, the logo goes to
   * this site's own landing page.
   */
  website?: string;
  /** Detect the locale from the browser instead of using `locale`. */
  detectLocale: boolean;
  /** Derive the currency from the locale instead of using `currency`. */
  detectCurrency: boolean;
  /** The partner loyalty programme the embed shows; unset shows nothing. */
  loyalty?: LoyaltyProgramme;
  /** Shown on sites that must give the EU linked travel arrangement notice. */
  linkedTravelNotice?: LinkedTravelNotice;
};
